import { FunctionalComponent } from 'preact';
import { useEffect, useLayoutEffect } from 'preact/hooks';
import {
  useSelector,
  useCheckoutContext,
} from '@primer-io/shared-library/contexts';
import { Scene, ElementID } from '../../enums/Checkout';
import { useDisableComponent } from '../../utils/hooks';
import { useWaitForComponent } from '../../utils/SceneLoader/WaitForComponents';
import CardIcons from './CardIcons';
import { CreditCard } from './CreditCard';
import { isCardholderNameVisible } from './utils';
import { getAttribute } from '../../checkout/DomUtilities';

const CreditCardForm: FunctionalComponent = () => {
  const { options, viewUtils, className } = useCheckoutContext();
  const isNameVisible = isCardholderNameVisible(options.card);

  const labels = useSelector((s) => s.translation);

  const inputLabelsVisible = useSelector((s) => s.form.inputLabelsVisible);

  const inputLabelClass = className('label', { hidden: !inputLabelsVisible });

  const cardholderNamePlaceholder = options.card?.cardholderName?.placeholder;

  const { wait, ready } = useWaitForComponent();
  const paymentMethods = useSelector((s) => s.paymentMethods);
  const showSavePaymentMethod = useSelector(
    (s) => s.options.showSavePaymentMethod,
  );

  const { disableControls } = useDisableComponent({
    scene: Scene.CHOOSE_PAYMENT_METHOD,
  });

  const card = paymentMethods.card as CreditCard;

  const disableIFrames = (disabled) => {
    card?.setDisabled(disabled);
  };

  const handleCardholderNameChange = () => {
    const inputValue = getAttribute(ElementID.CARDHOLDER_NAME_INPUT, 'value');
    if (inputValue?.trim()) {
      viewUtils.fieldUtils.setErrorState(ElementID.CARDHOLDER_NAME, null);
    }
  };

  useLayoutEffect(() => {
    wait('card');
  }, []);

  useEffect(() => {
    if (!card) return;
    (async () => {
      await card.mount();
      ready('card');
      disableIFrames(disableControls);
    })();
  }, [card]);

  useEffect(() => {
    disableIFrames(disableControls);
  }, [disableControls]);

  return (
    <div
      id='primer-checkout-card-form'
      class='PrimerCheckout__sceneElement PrimerCheckout__cardForm'
    >
      <div
        id='primer-checkout-card-number-field'
        class='PrimerCheckout__formField'
      >
        <label data-testId='CreditCardForm.inputLabel' class={inputLabelClass}>
          {labels?.cardNumber}
        </label>
        <div
          id='primer-checkout-card-number-input'
          class='PrimerCheckout__formInput PrimerCheckout__formInput--cardNumber'
        >
          <CardIcons />
        </div>

        <div
          id='primer-checkout-card-number-error'
          class='PrimerCheckout__helperText PrimerCheckout__helperText--error'
        ></div>
      </div>
      <div class='PrimerCheckout__formField PrimerCheckout__formField--dual'>
        <div
          id='primer-checkout-card-expiry-field'
          class='PrimerCheckout__formField'
        >
          <label
            data-testId='CreditCardForm.inputLabel'
            class={inputLabelClass}
          >
            {labels?.cardExpiry}
          </label>
          <div
            id='primer-checkout-card-expiry-input'
            class='PrimerCheckout__formInput'
          ></div>
          <div
            id='primer-checkout-card-expiry-error'
            class='PrimerCheckout__helperText PrimerCheckout__helperText--error'
          ></div>
        </div>
        <div
          id='primer-checkout-card-cvv-field'
          class='PrimerCheckout__formField'
        >
          <label
            data-testId='CreditCardForm.inputLabel'
            class={inputLabelClass}
          >
            {labels?.cardCVV}
          </label>

          <div
            id='primer-checkout-card-cvv-input'
            class='PrimerCheckout__formInput'
          ></div>
          <div
            id='primer-checkout-card-cvv-error'
            class='PrimerCheckout__helperText PrimerCheckout__helperText--error'
          ></div>
        </div>
      </div>
      {isNameVisible && (
        <div
          id='primer-checkout-cardholder-name-field'
          class='PrimerCheckout__formField'
        >
          <label
            data-testId='CreditCardForm.inputLabel'
            class={inputLabelClass}
            for='primer-checkout-cardholder-name-input'
          >
            {labels?.cardholderName}
          </label>
          <input
            id='primer-checkout-cardholder-name-input'
            class='PrimerCheckout__formInput'
            type='text'
            placeholder={
              cardholderNamePlaceholder ?? labels?.cardholderNamePlaceholder
            }
            autocomplete='cc-name'
            disabled={disableControls}
            onInput={handleCardholderNameChange}
          />
          <div
            id='primer-checkout-cardholder-name-error'
            class='PrimerCheckout__helperText PrimerCheckout__helperText--error'
          ></div>
        </div>
      )}
      {showSavePaymentMethod && (
        <div
          id='primer-checkout-save-payment-method-field'
          class='PrimerCheckout__formField PrimerCheckout__formField--orientation-horizontal'
        >
          <input
            id='primer-checkout-save-payment-method-input'
            type='checkbox'
            class='PrimerCheckout__checkbox'
          />
          <label
            for='primer-checkout-save-payment-method-input'
            class='PrimerCheckout__label PrimerCheckout__label--size-sm'
          >
            {labels?.savePaymentMethod}
          </label>
        </div>
      )}
    </div>
  );
};

export default CreditCardForm;
